import React, { useContext } from 'react';
import moment from 'moment';

import { NotificationsNone } from '@mui/icons-material';
import {
  Button,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useColorScheme,
} from '@mui/material';
import {
  alpha,
  lighten,
  useTheme,
} from '@mui/material/styles';

import { getReminderStatusOption } from '../../pages/settings/TaskPage/reminders/components/TagReminder/statusChip';
import ReminderContext from './context';
import {
  getPairKey,
  getReminderPairKey,
} from './helpers';

const HorseTaskReminderIndicator = ({
  tagId,
  eventTemplateId,
}) => {
  const context = useContext(ReminderContext);
  const theme = useTheme();
  const {
    mode,
    systemMode,
  } = useColorScheme();
  const darkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
  const pairKey = getPairKey(tagId, eventTemplateId);
  if (!context || !pairKey) return null;
  const {
    reminders,
    loading,
    error,
    reload,
    disabled,
    openReminder,
  } = context;
  if (loading) {
    return <Typography variant="caption" color="text.secondary" role="status">Checking horse reminder…</Typography>;
  }
  if (error) {
    return (
      <Button
        size="small"
        color="inherit"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          reload();
        }}
        sx={{ textTransform: 'none' }}
      >
        Reminder unavailable · Retry
      </Button>
    );
  }
  const matches = reminders.filter((reminder) => getReminderPairKey(reminder) === pairKey)
    .sort((left, right) => left.eventTemplateReminder.reminderDays - right.eventTemplateReminder.reminderDays || left.id - right.id);
  if (matches.length === 0) return null;
  return (
    <Stack
      direction="row"
      useFlexGap
      flexWrap="wrap"
      spacing={0.5}
      sx={{ maxWidth: '100%' }}
    >
      {matches.map((reminder) => {
        const option = getReminderStatusOption(reminder.status);
        const statusPalette = theme.palette[option.color] || theme.palette.grey;
        const statusMain = statusPalette.main || statusPalette[600];
        const statusColor = darkMode
          ? lighten(statusMain, 0.65)
          : statusPalette.dark || statusPalette[800];
        const backgroundOpacity = darkMode
          ? 0.1
          : 0.08;
        const activeOpacity = darkMode
          ? 0.18
          : 0.14;
        const definition = reminder.eventTemplateReminder;
        let label = `Horse reminder · ${option.label}`;
        if (matches.length > 1) label = `Horse reminder · Every ${definition.reminderDays} days · ${option.label}`;
        const dueDate = reminder.dueDate && moment(reminder.dueDate, moment.ISO_8601, true);
        if (dueDate && dueDate.isValid()) label += ` · Due ${dueDate.format('ll')}`;
        const accessibleLabel = `View ${definition.eventTemplate.name} reminder for ${reminder.tag.name}: ${label}`;
        return (
          <Tooltip key={reminder.id} title="View horse reminder. Status is based on saved tasks and schedules.">
            <Chip
              component="button"
              type="button"
              icon={<NotificationsNone />}
              label={label}
              aria-label={accessibleLabel}
              color={option.color}
              variant={option.variant || 'filled'}
              size="small"
              disabled={disabled}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                openReminder(reminder);
              }}
              sx={{
                height: 'auto',
                minHeight: 28,
                maxHeight: 'none',
                maxWidth: '100%',
                textAlign: 'left',
                '&&': {
                  color: statusColor,
                  borderColor: alpha(statusColor, 0.4),
                  backgroundColor: alpha(statusColor, backgroundOpacity),
                  '&:hover, &.Mui-focusVisible': { backgroundColor: alpha(statusColor, activeOpacity) },
                  '&.Mui-focusVisible': {
                    outline: `2px solid ${statusColor}`,
                    outlineOffset: 2,
                  },
                  '& .MuiChip-label, & .MuiChip-icon': { color: 'inherit' },
                },
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  py: 0.5,
                },
                '&::after': { content: '"›"' },
                pr: 1,
                '@media (pointer: coarse)': { minHeight: 44 },
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export default HorseTaskReminderIndicator;
